import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './apps/jobseeker/contexts/AuthContext';

// Lazy load the apps to improve performance
const JobSeekerApp = lazy(() => import('./apps/jobseeker/App'));
const HRApp = lazy(() => import('./apps/hr/App'));
const AdminApp = lazy(() => import('./apps/admin/App'));

// Public Pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/LoginPageV2')); // Updated to V2
const LoginPageV2 = lazy(() => import('./pages/LoginPageV2'));
const ForgotPassword = lazy(() => import('./apps/jobseeker/pages/Auth/ForgotPasswordPage'));
const Signup = lazy(() => import('./pages/SignupPageV2')); // Updated to V2
const Features = lazy(() => import('./pages/Features'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Security = lazy(() => import('./pages/Security'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const SharedTest = lazy(() => import('./apps/jobseeker/pages/SharedTest'));

// Legal Pages
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/Legal/TermsAndConditions'));
const CancellationsRefunds = lazy(() => import('./pages/Legal/CancellationsRefunds'));
const ShippingPolicy = lazy(() => import('./pages/Legal/ShippingPolicy'));

function App() {
    // Global Content Protection
    React.useEffect(() => {
        // 1. Disable Right-Click
        const handleContextMenu = (e) => {
            // Admin Exception REMOVED: Protection is now global
            e.preventDefault();
        };

        // 2. Disable Keyboard Shortcuts
        const handleKeyDown = (e) => {
            // Admin Exception REMOVED: Protection is now global 

            const key = e.key.toLowerCase();
            const isCtrl = e.ctrlKey || e.metaKey;
            const isShift = e.shiftKey;

            // Block F12 (DevTools)
            if (e.key === 'F12') {
                e.preventDefault();
                return;
            }

            // Block Ctrl+Shift+I (DevTools)
            if (isCtrl && isShift && key === 'i') {
                e.preventDefault();
                return;
            }

            // Block Ctrl+Shift+C (Inspect Element)
            if (isCtrl && isShift && key === 'c') {
                e.preventDefault();
                return;
            }

            // Block Ctrl+Shift+J (Console)
            if (isCtrl && isShift && key === 'j') {
                e.preventDefault();
                return;
            }

            // Block Ctrl+U (View Source)
            if (isCtrl && key === 'u') {
                e.preventDefault();
                return;
            }

            // Handle Copy/Paste/Select All (A, C, V, X)
            if (isCtrl && ['a', 'c', 'v', 'x'].includes(key)) {
                // Check HR Exceptions
                // "in upload jd page and on this schedule interviews page only crtl+c and crtl+v should work"
                // 1. Admin Exception: Give full clipboard/print/save access
                if (window.location.pathname.startsWith('/admin-panel')) {
                    return; // Allow
                }

                // 2. HR Exceptions (JD Upload & Interviews) - Only Copy/Paste
                const isHRExceptionPage = window.location.pathname.includes('/hr/jd-upload') ||
                    window.location.pathname.includes('/hr/interviews');

                if (isHRExceptionPage) {
                    // Allow ONLY C and V
                    if (key === 'c' || key === 'v') {
                        return; // Allow
                    }
                }

                // Block everything else (including Ctrl+A, Ctrl+X on HR pages, and all of them elsewhere)
                e.preventDefault();
            }
        };

        window.addEventListener('contextmenu', handleContextMenu);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('contextmenu', handleContextMenu);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <BrowserRouter>
            <AuthProvider>
                <Toaster position="top-center" />
                <Routes>
                    <Route path="/job-seeker/*" element={
                        <Suspense fallback={<div className="p-4">Loading Job Seeker Portal...</div>}>
                            <JobSeekerApp />
                        </Suspense>
                    } />

                    <Route path="/hr/*" element={
                        <Suspense fallback={<div className="p-4">Loading HR Portal...</div>}>
                            <HRApp />
                        </Suspense>
                    } />

                    <Route path="/admin-panel/*" element={
                        <Suspense fallback={<div className="p-4">Loading Admin Portal...</div>}>
                            <AdminApp />
                        </Suspense>
                    } />

                    {/* Public Routes */}
                    <Route path="/" element={<Suspense fallback={<div>Loading...</div>}><LandingPage /></Suspense>} />
                    <Route path="/login" element={<Suspense fallback={<div>Loading...</div>}><Login /></Suspense>} />
                    <Route path="/forgot-password" element={<Suspense fallback={<div>Loading...</div>}><ForgotPassword /></Suspense>} />
                    <Route path="/signup" element={<Suspense fallback={<div>Loading...</div>}><Signup /></Suspense>} />
                    <Route path="/features" element={<Suspense fallback={<div>Loading...</div>}><Features /></Suspense>} />
                    <Route path="/about" element={<Suspense fallback={<div>Loading...</div>}><About /></Suspense>} />
                    <Route path="/contact" element={<Suspense fallback={<div>Loading...</div>}><Contact /></Suspense>} />
                    <Route path="/security" element={<Suspense fallback={<div>Loading...</div>}><Security /></Suspense>} />
                    <Route path="/help-center" element={<Suspense fallback={<div>Loading...</div>}><HelpCenter /></Suspense>} />

                    {/* Shared Test Route - Outside of Auth */}
                    <Route path="/test/share/:testId" element={<Suspense fallback={<div>Loading Test...</div>}><SharedTest /></Suspense>} />

                    {/* Legal Routes */}
                    <Route path="/privacy" element={<Suspense fallback={<div>Loading...</div>}><PrivacyPolicy /></Suspense>} />
                    <Route path="/terms" element={<Suspense fallback={<div>Loading...</div>}><TermsAndConditions /></Suspense>} />
                    <Route path="/cancellations-refunds" element={<Suspense fallback={<div>Loading...</div>}><CancellationsRefunds /></Suspense>} />
                    <Route path="/shipping" element={<Suspense fallback={<div>Loading...</div>}><ShippingPolicy /></Suspense>} />
                </Routes>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
